import { forwardRef } from 'react';

/**
 * Shared glass-card primitive used across the dashboard. Matches the
 * "New Lead / Activate your AI / Incoming Call" reference visuals:
 *  - Dark gradient backdrop in dark mode (#0d0e10 with white/[0.04] sheen)
 *  - Soft blue glow underneath
 *  - Generous rounded-[20px] corners
 *  - 1px hairline border
 * Light mode falls back to a clean white card with the same rounding.
 */
export const GlassCard = forwardRef(function GlassCard({ className = '', children, glow = true, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={`relative bg-white border border-gray-200 dark:bg-[#0d0e10] dark:border-white/[0.06] rounded-[20px] ${glow ? 'shadow-[0_30px_60px_-30px_rgba(37,99,235,0.35)] dark:shadow-[0_30px_60px_-20px_rgba(37,99,235,0.25)]' : ''} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
});

/** Tiny uppercase tracked label. Used for section headers + field labels. */
export function CardLabel({ children, className = '' }) {
  return (
    <p className={`text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-slate-500 font-medium ${className}`}>
      {children}
    </p>
  );
}

/** Glass-card row: icon + label/value, used for the dashboard glass cards. */
export function GlassRow({ icon: Icon, label, value, accent = 'blue', className = '' }) {
  const ring = {
    blue: 'text-blue-500 dark:text-blue-400',
    emerald: 'text-emerald-500 dark:text-emerald-400',
    purple: 'text-purple-500 dark:text-purple-400',
    amber: 'text-amber-500 dark:text-amber-400',
  }[accent] || 'text-blue-400';
  return (
    <div className={`flex items-start gap-3 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-[14px] px-3.5 py-2.5 ${className}`}>
      {Icon && <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${ring}`} />}
      <div className="min-w-0 flex-1">
        <CardLabel>{label}</CardLabel>
        <p className="text-sm text-gray-900 dark:text-white truncate mt-0.5">{value}</p>
      </div>
    </div>
  );
}

/** Pill chip used for status / tag labels. */
export function Pill({ tone = 'neutral', children, className = '' }) {
  const tones = {
    neutral: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-white/[0.05] dark:text-slate-300 dark:border-white/[0.08]',
    hot: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30',
    blue: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30',
    purple: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30',
    amber: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30',
    red: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider rounded-full px-2.5 py-0.5 border ${tones[tone] || tones.neutral} ${className}`}>
      {children}
    </span>
  );
}

/** Gradient avatar circle (initials). Used in lead cards. */
export function GradientAvatar({ name, size = 44, className = '' }) {
  const initials = (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();
  // Deterministic gradient based on first char (so the same lead always gets the same color)
  const gradients = [
    'from-pink-500 to-purple-600',
    'from-blue-500 to-purple-600',
    'from-emerald-500 to-teal-600',
    'from-amber-500 to-orange-600',
    'from-cyan-500 to-blue-600',
    'from-fuchsia-500 to-pink-600',
  ];
  const idx = (initials.charCodeAt(0) || 0) % gradients.length;
  return (
    <div
      style={{ width: size, height: size }}
      className={`rounded-full bg-gradient-to-br ${gradients[idx]} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 ${className}`}
    >
      {initials}
    </div>
  );
}
